import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { PrinterOptionsSchema } from "../printnode/printer";

export enum PrintableDocument {
  PICK_LIST = "pickList",
  PACKING_SLIP = "packingSlip",
  LABEL = "label",
  COMMERCIAL_INVOICE = "commercialInvoice",
  PERSONALIZED_SLIP = "personalizedSlip",
  RETURN_LABEL = "returnLabel",
  LOCAL_PICK_UP_LABEL = "localPickUpLabel",
  BILL_OF_LADING = "billOfLading",
}

export enum PrintNodePrintJobStatus {
  NEW = "new",
  SENT_TO_CLIENT = "sent_to_client",
  ERROR = "error",
  DONE = "done",
  CANCELLED = "cancelled",
  DELETED = "deleted",
}

export enum PrintJobStatus {
  CREATED = "created",
  PURCHASED = "purchased",
  GENERATING = "generating",
  GENERATED = "generated",
  INITIALIZING_PRINTING = "initializingPrinting",
  SENT_TO_PRINT_NODE = "sentToPrintNode",
  PRINT_NODE_CONFIRMED = "printNodeConfirmed",
  SENT_TO_PRINTER = "sentToPrinter",
  COMPLETED = "completed",
  BATCH_COMPLETED = "batchCompleted",
  FAILED = "failed",
  CANCELLING = "cancelling",
  CANCELLED = "cancelled",
}

export const PrintStatusLabels: Record<PrintJobStatus, string> = {
  [PrintJobStatus.CREATED]: "Print job created",
  [PrintJobStatus.PURCHASED]: "Labels purchased",
  [PrintJobStatus.GENERATING]: "Generating PDF",
  [PrintJobStatus.GENERATED]: "PDF generated",
  [PrintJobStatus.INITIALIZING_PRINTING]: "Sending to print server",
  [PrintJobStatus.SENT_TO_PRINT_NODE]: "Sent to print server",
  [PrintJobStatus.PRINT_NODE_CONFIRMED]: "Print server confirmed",
  [PrintJobStatus.SENT_TO_PRINTER]: "Sent to printer",
  [PrintJobStatus.COMPLETED]: "Print job completed",
  [PrintJobStatus.BATCH_COMPLETED]: "Print job completed",
  [PrintJobStatus.FAILED]: "Print job failed",
  [PrintJobStatus.CANCELLING]: "Timing out print job",
  [PrintJobStatus.CANCELLED]: "Print job timed out",
};

const HumanReadablePrintJobErrors: [RegExp | string, string, boolean][] = [
  // PDF generation errors
  [/^generateDocumentPDF/, "Failed to generate PDF", true],
  ["No print node job id", "Failed to generate PDF", true],
  ["No url provided", "Failed to generate PDF", true],

  // Redo Connect crashes (various exit codes)
  [
    "worker exited with return code 3221226525",
    "Redo connect crashed, try restarting it",
    true,
  ],
  [
    "worker exited with return code 3221225477",
    "Redo connect crashed, try restarting it",
    true,
  ],
  [
    "worker exited with return code 3221226356",
    "Redo connect crashed, try restarting it",
    true,
  ],
  [
    "worker exited with return code -15",
    "Print worker was terminated, try restarting Redo Connect",
    true,
  ],
  [
    "unhandled_exception; [WinError -529697949]",
    "Redo connect crashed, try restarting it",
    true,
  ],

  // Printer configuration errors
  [
    /^printer '.*' not found on /,
    "Printer not found, check printer settings",
    false,
  ],
  [
    /^printer '.*' has been manually disabled$/,
    "Printer disabled, check printer settings",
    false,
  ],
  [
    "Win32 error 1801 from OpenPrinter()",
    "Invalid printer name, check printer settings",
    false,
  ],
  [
    "Win32 error 0 from DocumentProperties()",
    "Printer driver configuration error",
    false,
  ],

  // Printer service/connection errors
  [
    "Win32 error 1722 from OpenPrinter()",
    "Printer service unavailable, check printer connection",
    false,
  ],
  [
    "engine6_error; Unable to print document: An internal error occurred",
    "Printer internal error occurred",
    false,
  ],
  [
    "unhandled_exception; Unable to set current pm printer: -30880",
    "Printer connection issue. Your printer may be offline",
    false,
  ],

  // Network errors
  ["download failed", "Network error on printer", true],
  ["fetch failed", "Network error on printer", true],

  // Label purchase errors
  ["Activity task timed out", "Failed to purchase labels", false],
  ["Activity task failed", "Unknown failure, try again", true],
  ["Label purchase failed", "Failed to purchase labels", true],

  [
    "activity ScheduleToClose timeout",
    "Transient printing error. Please try again.",
    true,
  ],
  ["HTTP 403", "Transient printing error. Please try again.", true],
  [
    "Gotenberg returned 503",
    "Transient printing error. Please try again.",
    true,
  ],
  [
    "Gotenberg returned 400",
    "Transient printing error. Please try again.",
    true,
  ],

  // Success status
  ["done", "Print job completed", false],
];

export function getHumanReadablePrintJobError(error?: string) {
  if (!error) {
    return;
  }

  for (const [regex, label] of HumanReadablePrintJobErrors) {
    if (regex instanceof RegExp && regex.test(error)) {
      return label;
    }
    if (typeof regex === "string" && error.includes(regex)) {
      return label;
    }
  }
  return error;
}

export function isRetryablePrintJobError(error?: string) {
  if (!error) {
    return false;
  }

  for (const [regex, _, retryable] of HumanReadablePrintJobErrors) {
    if (regex instanceof RegExp && regex.test(error)) {
      return retryable;
    }
    if (typeof regex === "string" && error.includes(regex)) {
      return retryable;
    }
  }
  return false;
}

export const RedoPrintJobEventSchema = z.object({
  timestamp: z.date(),
  status: createEnum(PrintJobStatus, { enumName: "PrintJobStatus" }),
  message: z.string().optional(),
});

export type RedoPrintJobEvent = z.infer<typeof RedoPrintJobEventSchema>;

export const RedoPrintJobBaseSchema = z.object({
  _id: zExt.objectId(),
  printNodeJobId: z.number().optional(),
  userId: z.string(),
  teamId: z.string(),
  url: z.string().optional(),
  documentType: createEnum(PrintableDocument, {
    enumName: "PrintableDocument",
  }),
  templateId: z.string().optional(),
  printerId: z.number().optional(),
  printerOptions: PrinterOptionsSchema.optional(),
  status: createEnum(PrintJobStatus, { enumName: "PrintJobStatus" }),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  printBatchCompletedAt: z.date().nullish(),
});

export const RedoPrintJobDataSchema = z.union([
  z.object({ fulfillmentGroupIds: z.array(z.string()) }),
  z.object({ batchId: z.string() }),
]);

export const RedoPrintJobSchema = z.intersection(
  RedoPrintJobDataSchema,
  z.object({
    ...RedoPrintJobBaseSchema.shape,
    events: z.array(RedoPrintJobEventSchema),
  }),
);

export const RedoPrintJobOmitEventsSchema = z.intersection(
  RedoPrintJobDataSchema,
  RedoPrintJobBaseSchema,
);

const CreateRedoPrintJobSchema = z.intersection(
  RedoPrintJobDataSchema,
  z.object({
    ...RedoPrintJobBaseSchema.omit({ _id: true, status: true }).shape,
    _id: zExt.objectId().optional(),
  }),
);

export type CreateRedoPrintJob = z.infer<typeof CreateRedoPrintJobSchema>;

export type RedoPrintJob = z.infer<typeof RedoPrintJobSchema>;
